/**
 * ReservationSettings — Slot-Reservation Konfiguration (Iter 309)
 *
 * Admin-Setting: Steuert wie lange ein vom Chatbot vorgeschlagener
 * Termin-Slot für einen Kunden "geblockt" bleibt, bis dieser bestätigt.
 *
 * User-Wunsch Markus (06/2026):
 *   • Konfigurierbar pro Studio (Default 180 min = 3h)
 *   • Min 30 min, Max 24 h
 *   • Kann komplett deaktiviert werden (dann buchst der Bot direkt
 *     ohne Hold — riskant bei vagen Anfragen).
 */
import React, { useCallback, useEffect, useState } from 'react';import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Save, Timer, Info } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReservationSettings = () => {
  const [settings, setSettings] = useState({
    enabled: true,
    hold_duration_minutes: 180,
  });
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/reservation-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings({
        enabled: res.data?.enabled ?? true,
        hold_duration_minutes: res.data?.hold_duration_minutes ?? 180,
      });
    } catch (e) {
      // Defaults verwenden, kein blockender Fehler
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/admin/reservation-settings/default`,
        {
          enabled: settings.enabled,
          hold_duration_minutes: parseInt(settings.hold_duration_minutes, 10),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reservation-Einstellungen gespeichert');
    } catch (e) {
      toast.error(`Fehler: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const hours = Math.floor(settings.hold_duration_minutes / 60);
  const minutes = settings.hold_duration_minutes % 60;
  const humanReadable = hours > 0
    ? `${hours}h${minutes > 0 ? ` ${minutes}min` : ''}`
    : `${minutes} min`;

  return (
    <Card data-testid="reservation-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Timer className="h-5 w-5 text-amber-600" />
          Slot-Reservation (Chatbot-Verschiebungen)
        </CardTitle>
        <CardDescription>
          Wenn der KI-Bot einem Kunden alternative Termine vorschlägt
          (z.B. bei Verschiebungs-Anfragen), werden diese für eine
          festgelegte Zeit reserviert. Kunde bekommt WhatsApp mit Ablauf-Hinweis.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <Label className="text-sm font-semibold">Reservation aktiv</Label>
            <p className="text-xs text-gray-500 mt-1">
              Wenn aktiv: vorgeschlagene Slots werden geblockt bis Bestätigung.
              <br />
              Wenn aus: Slots bleiben offen, andere Kunden könnten sie buchen.
            </p>
          </div>
          <Switch
            data-testid="reservation-enabled-switch"
            checked={settings.enabled}
            onCheckedChange={(v) => setSettings(s => ({ ...s, enabled: v }))}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="hold-duration" className="text-sm font-semibold">
            Hold-Dauer (Minuten)
          </Label>
          <div className="flex items-center gap-3">
            <Input
              id="hold-duration"
              data-testid="hold-duration-input"
              type="number"
              min={30}
              max={1440}
              step={15}
              value={settings.hold_duration_minutes}
              onChange={(e) => setSettings(s => ({
                ...s,
                hold_duration_minutes: e.target.value,
              }))}
              className="w-32"
              disabled={!settings.enabled}
            />
            <span className="text-sm text-gray-600">≈ {humanReadable}</span>
          </div>
          <div className="grid grid-cols-4 gap-2 mt-2">
            {[60, 120, 180, 360].map((m) => (
              <Button
                key={m}
                type="button"
                size="sm"
                variant={settings.hold_duration_minutes === m ? 'default' : 'outline'}
                onClick={() => setSettings(s => ({ ...s, hold_duration_minutes: m }))}
                disabled={!settings.enabled}
                data-testid={`preset-${m}min`}
              >
                {m < 60 ? `${m}min` : `${m / 60}h`}
              </Button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Erlaubt: 30 Minuten bis 24 Stunden. Default: 3 Stunden.
          </p>
        </div>

        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <strong>So funktioniert&apos;s:</strong>
              <ol className="list-decimal ml-4 mt-1 space-y-0.5">
                <li>Kunde schreibt &quot;Ich kann am Montag nicht, hast du Dienstag oder Donnerstag?&quot;</li>
                <li>Bot prüft beide Tage, schlägt pro Tag den besten 1 Slot vor</li>
                <li>Beide Slots werden für <strong>{humanReadable}</strong> reserviert</li>
                <li>Kunde bekommt WhatsApp mit Bestätigungs-Buttons</li>
                <li>Bei Bestätigung → Original-Termin storniert, neuer aktiviert, andere Reservierung freigegeben</li>
                <li>Bei keiner Antwort → automatisches Ablaufen, Slots wieder frei</li>
              </ol>
            </div>
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-reservation-settings"
          className="w-full"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Speichern...' : 'Einstellungen speichern'}
        </Button>

        {/* Test-Tool für Admins */}
        <ReschedulePreviewTool />
      </CardContent>
    </Card>
  );
};

const ReschedulePreviewTool = () => {
  const [message, setMessage] = useState(
    'Hola Alex, ich muss nächsten Montag leider schieben. ' +
    'Wie sieht es nächste Woche bei dir am Dienstag oder Donnerstag Morgen aus?'
  );
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const runPreview = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/admin/chatbot/reschedule/preview`,
        { message },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
    } catch (e) {
      toast.error(`Fehler: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3" data-testid="reschedule-preview-tool">
      <div className="flex items-center gap-2 text-sm font-semibold text-blue-900">
        <Info className="h-4 w-4" />
        NLP-Vorschau (Testen ohne Kunde)
      </div>
      <p className="text-xs text-blue-800">
        Tippe hier eine Nachricht ein, um zu sehen wie der Bot sie interpretiert.
        Erzeugt KEINE echten Reservierungen.
      </p>
      <textarea
        data-testid="reschedule-preview-input"
        className="w-full text-sm p-2 border border-blue-300 rounded font-mono"
        rows={3}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
      />
      <Button
        size="sm"
        variant="outline"
        onClick={runPreview}
        disabled={loading || !message.trim()}
        data-testid="reschedule-preview-run"
      >
        {loading ? 'Analysiere…' : 'Vorschau anzeigen'}
      </Button>
      {result && (
        <div className="bg-white rounded p-3 text-xs space-y-2 border border-blue-200" data-testid="reschedule-preview-result">
          <div>
            <strong>Verschiebungs-Intent erkannt:</strong>{' '}
            <span className={result.is_reschedule ? 'text-green-700' : 'text-gray-500'}>
              {result.is_reschedule ? 'JA ✓' : 'NEIN'}
            </span>
          </div>
          {result.is_reschedule && (
            <>
              <div>
                <strong>Wunsch-Tage:</strong>{' '}
                {result.target_dates.length === 0
                  ? <span className="text-amber-700">keine erkannt — Bot würde nachfragen</span>
                  : result.target_dates.map((d) => (
                      <span key={d} className="inline-block bg-amber-100 px-2 py-0.5 rounded mr-1">
                        {new Date(d).toLocaleDateString('de-CH', { weekday: 'long', day: '2-digit', month: '2-digit' })}
                      </span>
                    ))
                }
              </div>
              {result.time_preference && (
                <div>
                  <strong>Tageszeit:</strong> {result.time_preference}
                </div>
              )}
              <div>
                <strong>Aktion:</strong> <code>{result.action_plan}</code>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ReservationSettings;
